import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ProgressCircle, Button, Tooltip, TooltipTrigger, ActionButton, TextField } from '@adobe/react-spectrum'
import tableStyles from '../Theme/Styles/table.module.css';
import licenseTableStyles from '../Theme/Styles/licenseTable.module.css';
import localization from '../Language/localization';


const CustomerLicense = (prop: any) => {
    const { updateScroller: callBackEvent, isCIP } = prop;
    const [showButton, canButtonVisible] = useState(false);
    const [loading, updateLoading] = useState(true);
    const [lengthAllowed, setLengthOfTable] = useState(200);
    const [rows, updateRows] = useState([]);
    const [originalRows, setOriginalRows] = useState([]);
    const [firstRow, updateFirstRows] = useState({ Individual: 0, Team: 0 });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' }); // Start with desc for max to min
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef<any>(null);
    const { customerLicenseCount, language, darkMode }: any = useSelector((state: any) => state.customerInfo);

    useEffect(() => {
        const { loading, data } = customerLicenseCount;
        const filteredData = data.filter((item: any) => item.type === "DOMAIN");
        const firstRow = data.find((item: any) => item.type === "FIRMO");
        setOriginalRows(filteredData);
        updateRows(filteredData);
        updateFirstRows(firstRow);
        setSortConfig({ key: null, direction: 'desc' }); // Reset to desc for max to min
        
        // Clear search when license data changes (happens on conversation switch)
        setSearchTerm('');
        setShowSearch(false);
        
        // if (data && data.length === 0) {
        //     canButtonVisible(false)
        //     setLengthOfTable(0)
        // }
        // else if (data && data.length <= 4) {
        //     setLengthOfTable(data.length)
        //     canButtonVisible(false)
        // } else {
        //     setLengthOfTable(4)
        //     canButtonVisible(true)
        // }
        updateLoading(loading)
        setTimeout(() => {
            callBackEvent('UPDATE_LISENCE_SCRILLONG');
        }, 1000);
    }, [customerLicenseCount]);

    // Auto-focus search input when search is shown
    useEffect(() => {
        if (showSearch && searchInputRef.current) {
            setTimeout(() => {
                try {
                    // Try to focus using Adobe React Spectrum TextField methods
                    if (searchInputRef.current.getInputElement) {
                        searchInputRef.current.getInputElement().focus();
                    } else if (searchInputRef.current.focus) {
                        searchInputRef.current.focus();
                    }
                } catch (error) {
                    console.log('Unable to focus search input:', error);
                }
            }, 100);
        }
    }, [showSearch]);

    const handleSort = (key: string) => {
        let direction = 'desc'; // Default to desc (max to min) for first click
        
        // If we're clicking the same column that's already sorted
        if (sortConfig.key === key) {
            // Toggle direction: desc -> asc -> desc -> asc...
            direction = sortConfig.direction === 'desc' ? 'asc' : 'desc';
        }
        // If it's a different column or first time, start with desc (max to min)
        
        setSortConfig({ key, direction });

        // Sort the currently displayed rows (filtered data if search is active)
        const dataToSort = searchTerm.trim() !== '' ? rows : originalRows;
        
        // Ensure we have data to sort
        if (!dataToSort || dataToSort.length === 0) {
            return;
        }
        
        const sortedRows = [...dataToSort].sort((a, b) => {
            const aValue = parseInt(a[key]) || 0;
            const bValue = parseInt(b[key]) || 0;
            
            if (direction === 'asc') {
                return aValue - bValue; // min to max
            } else {
                return bValue - aValue; // max to min
            }
        });

        updateRows(sortedRows);
        setTimeout(() => {
            callBackEvent('UPDATE_LISENCE_SCRILLONG');
        }, 100);
    };

    const getSortIcon = (columnKey: string) => {
        if (sortConfig.key !== columnKey) {
            return '^'; // Default icon when not sorted (indicates max to min default)
        }
        return sortConfig.direction === 'desc' ? '^' : 'v'; // ^ for max to min (desc), v for min to max (asc)
    };

    const handleSearchClick = () => {
        setShowSearch(true);
    };

    const handleSearchClose = () => {
        setShowSearch(false);
        setSearchTerm('');
        updateRows(originalRows);
        setSortConfig({ key: null, direction: 'desc' }); // Reset sort when closing search
        setTimeout(() => {
            callBackEvent('UPDATE_LISENCE_SCRILLONG');
        }, 100);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        if (value.trim() === '') {
            updateRows(originalRows);
        } else {
            const filteredRows = originalRows.filter((row: any) => 
                row.product.toLowerCase().includes(value.toLowerCase())
            );
            updateRows(filteredRows);
        }
        // Reset sort when search changes
        setSortConfig({ key: null, direction: 'desc' });
        setTimeout(() => {
            callBackEvent('UPDATE_LISENCE_SCRILLONG');
        }, 100);
    };

    const getTeamsHeaderText = (text: string, applyCIPLimit: boolean = false) => {
        // Only apply character limit for CIP, not SCIP
        if (!applyCIPLimit) {
            return { displayText: text, needsTooltip: false };
        }
        
        if (text.length <= 7) {
            return { displayText: text, needsTooltip: false };
        }
        return { displayText: text.substring(0, 5) + '..', needsTooltip: true };
    };

    const getTruncatedText = (text: string, maxVisualWidth: number = 21, applyCIPLimit: boolean = false) => {
        // Only apply character limit for CIP, not SCIP
        if (!applyCIPLimit) {
            return text;
        }
        
        let visualWidth = 0;
        let truncatedText = '';
        
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const charCode = char.charCodeAt(0);
            
            // Check if character is CJK (Chinese, Japanese, Korean)
            const isCJK = (charCode >= 0x4E00 && charCode <= 0x9FFF) || // CJK Unified Ideographs (Chinese/Kanji)
                         (charCode >= 0x3040 && charCode <= 0x309F) || // Hiragana (Japanese)
                         (charCode >= 0x30A0 && charCode <= 0x30FF) || // Katakana (Japanese)
                         (charCode >= 0xAC00 && charCode <= 0xD7AF) || // Hangul Syllables (Korean)
                         (charCode >= 0x3130 && charCode <= 0x318F) || // Hangul Compatibility Jamo (Korean)
                         (charCode >= 0x3200 && charCode <= 0x32FF) || // Enclosed CJK Letters and Months
                         (charCode >= 0x3400 && charCode <= 0x4DBF) || // CJK Extension A
                         (charCode >= 0x20000 && charCode <= 0x2A6DF) || // CJK Extension B
                         (charCode >= 0xFF00 && charCode <= 0xFFEF) || // Fullwidth characters
                         (charCode >= 0x3000 && charCode <= 0x303F);   // CJK Symbols and Punctuation
            
            const charWidth = isCJK ? 2 : 1;
            
            if (visualWidth + charWidth > maxVisualWidth) {
                return truncatedText + '...';
            }
            
            visualWidth += charWidth;
            truncatedText += char;
        }
        
        return text;
    };

    // const handlePress = () => {
    //     if (rows.length !== lengthAllowed) {
    //         setLengthOfTable(rows.length)
    //         callBackEvent('UPDATE_LISENCE_SCRILLONG');
    //     } else {
    //         setLengthOfTable(4)
    //         callBackEvent('UPDATE_LISENCE_SCRILLONG');
    //     }
    // };




    return (
        loading ? <ProgressCircle aria-label='Loading…' isIndeterminate alignSelf="center" marginStart="50%" /> :
            originalRows && originalRows.length > 0 ?
                <div >
                    <table className={darkMode ? `${tableStyles['custom-table']} ${tableStyles['table-dark']}` : `${tableStyles['custom-table']}`}>
                        <thead className={tableStyles['thead']}>
                            <tr>
                                <th className={`${licenseTableStyles['productNameHeader']}`} key={"productNameHeader"}>
                                    {showSearch ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                                            <TextField
                                                placeholder={localization[language]['page.search.placeholder']}
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                                isQuiet
                                                UNSAFE_style={{
                                                    flex: 1,
                                                    minWidth: '100px',
                                                    fontSize: '12px',
                                                    border: 'none !important',
                                                    backgroundColor: 'transparent !important',
                                                    outline: 'none !important',
                                                    boxShadow: 'none !important',
                                                    borderBottom: 'none !important',
                                                    borderTop: 'none !important',
                                                    borderLeft: 'none !important',
                                                    borderRight: 'none !important'
                                                }}
                                                UNSAFE_className={licenseTableStyles.noFocusOutline}
                                                ref={searchInputRef}
                                            />
                                            <Button
                                                variant="primary"
                                                isQuiet
                                                onPress={handleSearchClose}
                                                UNSAFE_style={{
                                                    minWidth: '20px',
                                                    height: '20px',
                                                    padding: '0',
                                                    margin: '0',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    color: '#666'
                                                }}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    ) : (
                                        <div 
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'flex-start', 
                                                gap: '8px'
                                            }}
                                        >
                                            <span
                                                onClick={handleSearchClick}
                                                style={{
                                                    cursor: 'pointer',
                                                    userSelect: 'none'
                                                }}
                                                title="Click to search products"
                                            >
                                                {localization[language]['page.customer.license.productName']}
                                            </span>
                                            <Button
                                                variant="primary"
                                                isQuiet
                                                onPress={handleSearchClick}
                                                UNSAFE_style={{
                                                    minWidth: '24px',
                                                    height: '24px',
                                                    padding: '0',
                                                    margin: '0',
                                                    fontSize: '16px',
                                                    cursor: 'pointer',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    transform: 'rotate(-45deg)'
                                                }}
                                            >
                                                ⚲
                                            </Button>
                                        </div>
                                    )}
                                </th>
                                <th className={`${licenseTableStyles['individualHeader']}`} key={"individualHeader"}>
                                    <div 
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: '0px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleSort('Individual')}
                                    >
                                        <span style={{ cursor: 'pointer' }}>
                                            {localization[language]['page.customer.license.individual']}
                                        </span>
                                        <Button
                                            variant="primary"
                                            isQuiet
                                            onPress={() => handleSort('Individual')}
                                            UNSAFE_style={{
                                                minWidth: '12px',
                                                height: '12px',
                                                padding: '0',
                                                margin: '0',
                                                fontSize: '10px',
                                                cursor: 'pointer',
                                                backgroundColor: 'transparent',
                                                border: 'none'
                                            }}
                                        >
                                            {getSortIcon('Individual')}
                                        </Button>
                                    </div>
                                </th>
                                <th className={`${licenseTableStyles['teamsHeader']}`} key={"teamsHeader"}>
                                    <div 
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: '0px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleSort('Team')}
                                    >
                                        {(() => {
                                            const teamsText = getTeamsHeaderText(localization[language]['page.customer.license.teams'], isCIP);
                                            return (
                                                <span 
                                                    style={{ cursor: 'pointer' }}
                                                    title={teamsText.needsTooltip ? localization[language]['page.customer.license.teams'] : undefined}
                                                >
                                                    {teamsText.displayText}
                                        </span>
                                            );
                                        })()}
                                        <Button
                                            variant="primary"
                                            isQuiet
                                            onPress={() => handleSort('Team')}
                                            UNSAFE_style={{
                                                minWidth: '12px',
                                                height: '12px',
                                                padding: '0',
                                                margin: '0',
                                                fontSize: '10px',
                                                cursor: 'pointer',
                                                backgroundColor: 'transparent',
                                                border: 'none'
                                            }}
                                        >
                                            {getSortIcon('Team')}
                                        </Button>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className={tableStyles['tbody']}>
                        {firstRow && <tr >
                                <td className={`${licenseTableStyles['productNameCell']}`}>
                                    <TooltipTrigger delay={300}>
                                        <ActionButton UNSAFE_className={licenseTableStyles['actionButton']} isQuiet top='0' bottom='0' left='0'><strong>{getTruncatedText(localization[language]['page.customer.license.total'], 21, isCIP)}</strong></ActionButton>
                                        <Tooltip>{localization[language]['page.customer.license.total']}</Tooltip>
                                    </TooltipTrigger></td>
                                <td className={`${licenseTableStyles[isCIP ? 'individualCell_CIP' : 'individualCell_SCIP']}`}><strong>{firstRow?.Individual || '0'}</strong></td>
                                <td className={`${licenseTableStyles[isCIP ? 'teamsCell_CIP' : "teamsCell_SCIP"]}`}><strong>{firstRow?.Team || "0"}</strong></td>
                            </tr>
                            }
                            {rows.map(
                                (data: any, index: number) =>
                                    index < lengthAllowed && (
                                        <tr key={index} >
                                            <td className={`${licenseTableStyles['productNameCell']}`}>
                                                <TooltipTrigger delay={300}>
                                                    <ActionButton UNSAFE_className={licenseTableStyles['actionButton']} isQuiet top='0' bottom='0' left='0'>{data.product}</ActionButton>
                                                    <Tooltip>{data.product}</Tooltip>
                                                </TooltipTrigger></td>
                                            <td className={`${licenseTableStyles[isCIP ? 'individualCell_CIP' : 'individualCell_SCIP']}`}>{data.Individual}</td>
                                            <td className={`${licenseTableStyles[isCIP ? 'teamsCell_CIP' : "teamsCell_SCIP"]}`}>{data.Team}</td>
                                        </tr>
                                    )
                            )}
                        </tbody>
                    </table>
                    {/* <div className={showButton ? licenseTableStyles['buttonContainer'] : ''}>
                        {showButton && <Button UNSAFE_className={licenseTableStyles['viewButton']} variant="primary" onPress={handlePress}>{rows.length === lengthAllowed ? localization[language]['viewLess'] : localization[language]['viewAll']}</Button>}
                    </div> */}
                </div> : <div className={licenseTableStyles['noLicense']}>{localization[language]['page.customer.license.notfound']}</div>
    )

};

export default CustomerLicense;